import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import AccountCurrency from '../../shared/AccountCurrency';
import DifferenceSection from '../../shared/DifferenceSection';
import SupportingDocuments from '../../shared/SupportingDocuments';
import PaymentVouchersForm from './PVPopupFormSection';
import { makeStyles } from 'tss-react/mui';
import { cloneDeep } from 'lodash';
import {
  enumListAsRecordObject,
  lookupListAsRecordObject,
} from '@utils/graph-utils';
import {
  createPVPaymentMutation,
  getPaymentPayablesMutation,
} from '../queries';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { MAIN_THEME } from '@styles';
import {
  IAddVoucherFormPopup,
  IAddVoucherLOVs,
  IVoucherPopupTableData,
  IVoucherPopupValues,
} from '../../shared/types';
import { CurrencySymbolsAsRecordObject, mapToPopupListingData } from '../utils';
import { initialErrors, initialLovs, initialValues } from '.';
import { KeyOf, updateFormField, ValueOf } from '@utils/helper-utils';
import { IListingData } from '@models/listing';
import { getUserToken } from '@utils/userUtils';
import { resolveGraphqlBaseUrl } from '@utils/tenant-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import Loader from '@components/loader/Loader';
import { mapToPaymentDetails } from '../../shared/utils';
import { modifyPaymentMutation } from '../../shared/queries';
import { isEmpty } from '@utils/validationUtils';
import { useMutation } from '@apollo/client';
import TextAreaFormField from '@components/form-fields/TextAreaFormField';
import EnhancedButton from '@components/button/EnhancedButton';
import { IAddVoucherErrors } from '..';
import DateService from '@services/DateService';
import { IFile } from '@models/file';
import { useAppSelector } from '@redux/hooks';

const useStyles = makeStyles()(() => ({
  inputsRow: {
    display: 'grid',
    gridTemplateColumns: `repeat(4, 23.5%)`,
    gap: '2%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonsContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignContent: 'center',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  labelSelector: {
    lineHeight: '21px !important',
    marginBottom: '12.54px !important',
    fontSize: `${MAIN_THEME.fontSize.rg} !important`,
    fontFamily: `${MAIN_THEME.font.medium} !important`,
  },
  inputSelector: {
    height: '41px',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '20px',
    alignItems: 'center',
  },
  section: {
    display: 'grid',
    gridTemplateColumns: '6fr 6fr',
    gridColumnGap: '21px',
  },
  uploadedFileContainer: {
    border: `1px solid ${MAIN_THEME.color.white92}`,
    padding: '8px 13px 8px 16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
    margin: '0 0 10px',
  },
  uploadedFileName: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '20px',
    fontFamily: MAIN_THEME.font.medium,
    margin: '0 auto 0 0',
  },
  uploadedFileClearIcon: {
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    padding: '0',
    margin: '0 0 0 20px',
  },
  documentSection: {
    width: '281px',
  },
  totalContainer: {
    display: 'flex',
    justifyContent: 'end',
  },
}));

const PaymentVoucherFormPopup: React.FC<IAddVoucherFormPopup> = ({
  data,
  onSuccess,
  onClose,
  currentPaymentId,
  detailsLoading,
  isEdit = false,
}) => {
  const businessPartnerId = useParams()?.id;
  const { classes } = useStyles();
  const [loading, setLoading] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [values, setValues] = useState<IVoucherPopupValues>(initialValues);
  const [errors, setErrors] = useState<IAddVoucherErrors>(initialErrors);
  const [lovs, setLovs] = useState<IAddVoucherLOVs>(initialLovs);
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageNumber: 1,
    pageSize: 5,
    totalCount: 0,
  });
  const [getPaymentPayables] = useMutation(getPaymentPayablesMutation);

  const { currency } = useAppSelector((state) => state.lovs);
  const currenciesSymbols = useMemo(() => {
    return lookupListAsRecordObject(currency, false, 'Code', 'Symbol');
  }, [currency]);

  const handleChange = (
    name: KeyOf<IVoucherPopupValues>,
    value: ValueOf<IVoucherPopupValues>
  ) => {
    let newValues = cloneDeep(values);
    newValues = updateFormField(newValues, name, value);
    setValues(newValues);
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();

      if (isEdit && currentPaymentId) {
        formData.append(
          'operations',
          JSON.stringify({
            query: modifyPaymentMutation,
            variables: {
              currentPaymentID: currentPaymentId,
              note: values.note,
              PersistDocuments: values.incomingDocuments.map(
                (doc: IFile) => doc.id
              ),
              UploadedDocuments: Array.from(
                { length: values.supportingDocuments.length },
                (): unknown => null
              ).fill(null),
            },
          })
        );
      } else {
        formData.append(
          'operations',
          JSON.stringify({
            query: createPVPaymentMutation,
            variables: {
              paymentDate: DateService.formatDateBackend(values.dateOfPayment),
              paymentCurrency: values.currency,
              paymentType: 'PAYABLE',
              paymentMethod: values.paymentMethod,
              referenceNumber: values.referenceNum,
              totalAmount: Number(values.totalAmount),
              totalAmountUnallocated: values.totalAmountUnAllocated,
              currentBusinessPartnerID: businessPartnerId,
              totalDebit: values.totalDebit,
              totalCredit: values.totalCredit,
              note: values.note,
              pvList: Object.keys(tableData.pagedItems)
                .filter(
                  (key: string) => !isEmpty(tableData.pagedItems[key]?.amount)
                )
                .map((key: string) => {
                  const item: IVoucherPopupTableData =
                    tableData.pagedItems[key];
                  return {
                    AmountOutstanding: item.amountOutstanding,
                    AmountOutstandingCurrency: item.amountOutstandingCurrency,
                    AmountPaid: Number(item.amount),
                    BillID: item.billId,
                    BillNumber: item.billNum,
                    CommissionDue: item.amountDue,
                    DueDate: DateService.formatDateBackend(item.dueDate),
                    PolicyID: item.policyId,
                    PolicyNumber: item.policyNum,
                  };
                }),
              transactionList: values.transactions.map((item) => {
                return {
                  AccountCredit: item.accountCredit,
                  AccountDebit: item.accountDebit,
                  AccountID: item.accountId,
                  AccountName: item.accountName,
                  DebitOrCredit: item.debitOrCredit,
                  Description: item.description,
                  SystemCredit: item.systemCredit,
                  SystemDebit: item.systemDebit,
                  SystemSecondaryCredit: item.systemSecondaryCredit,
                  SystemSecondaryDebit: item.systemSecondaryDebit,
                  TransactionCurrency:
                    item.transactionCurrency ||
                    values.systemCurrencies.primary.Code,
                };
              }),
              UploadedDocuments: Array.from(
                { length: values.supportingDocuments.length },
                (): unknown => null
              ).fill(null),
            },
          })
        );
      }

      if (!isEmpty(values.supportingDocuments)) {
        const fileMap = values.supportingDocuments.reduce(
          (acc: Record<number, string[]>, file, index) => {
            acc[index] = [`variables.UploadedDocuments.${index}`];
            return acc;
          },
          {}
        );

        formData.append('map', JSON.stringify(fileMap));

        values.supportingDocuments.forEach((file, index) => {
          formData.append(index.toString(), file, file.name);
        });
      } else {
        formData.append('map', JSON.stringify({}));
      }

      const options = {
        method: 'POST',
        headers: {
          authorization: `Bearer ${getUserToken()}` || null,
          'GraphQL-preflight': '1',
        },
        body: formData,
      };

      const response = await fetch(
        `${resolveGraphqlBaseUrl()}/graphql`,
        options
      );
      const data = await response.json();

      if (!data.errors) {
        toast.success(
          <ToastSuccessMessage>
            {isEdit
              ? 'Payment successfully updated!'
              : 'Payment successfully created!'}
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 500);
      } else {
        toast.error(
          <ToastErrorMessage>{data.errors[0].message}</ToastErrorMessage>
        );
      }
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const initialize = async () => {
    try {
      setLoading(true);

      const res = data.values;

      if (!res) return;

      const paymentMethods = enumListAsRecordObject(
        res.Accounting_PaymentMethodList.enumValues
      );

      const currencies = res.Core.queries.getSystemCurrencies[0];

      let newValues = cloneDeep(values);

      if (isEdit && data.queries) {
        const paymentDetails = mapToPaymentDetails(data.queries);

        newValues = {
          ...newValues,
          ...paymentDetails,
        };

        setTableData({
          pagedItems: paymentDetails.payments,
          pageNumber: 1,
          pageSize: 5,
          totalCount: Object.keys(paymentDetails.payments).length,
        });
        setIsSubmitDisabled(false);
      } else if (!isEdit) {
        const res = await getPaymentPayables({
          variables: {
            selectedCurrency: null,
            selectedPaymentDate: null,
            currentBusinessPartnerID: businessPartnerId,
          },
          errorPolicy: 'all',
        });

        if (res.errors && res.errors.length > 0) {
          if (res.errors[0]?.extensions?.code === 'CurrencyExchangeRateNull') {
            toast.error(
              <ToastErrorMessage>{res.errors[0].message}</ToastErrorMessage>
            );
            setTableData({
              pagedItems: {},
              pageNumber: 1,
              pageSize: 5,
              totalCount: 0,
            });
          }
          return;
        }

        const popupData = mapToPopupListingData(
          res.data.accounting.actions.getPaymentPayables.PaymentBill,
          currenciesSymbols
        );
        setErrors((prev) => ({
          ...prev,
          tableAmountPaid: Array.from(
            { length: Object.keys(popupData).length },
            (): string => ''
          ),
        }));
        setTableData({
          pagedItems: popupData,
          pageNumber: 1,
          pageSize: 5,
          totalCount: Object.keys(popupData).length,
        });
      }

      newValues.companyId =
        res.SalesforceManagement.entities.businessPartner.views.SalesforceManagement_all.properties.RelatedCompany.Id;

      newValues.systemCurrencies = {
        primary: currencies.core_SystemCurrency_BasicCurrency,
        secondary: currencies.core_SystemCurrency_SecondaryCurrency,
      };

      setValues(newValues);

      setLovs((prev) => ({
        ...prev,
        currenciesSymbols,
        paymentMethods,
      }));
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initialize();
  }, [data]);

  const validateForm = () => {
    // Check if any of the fields are invalid
    // If so, return true to disable the submit button
    const invalid =
      values.totalAmountUnAllocated !== 0 ||
      Math.abs(Number(values.totalDebit) - Number(values.totalCredit)) !== 0 ||
      isSubmitDisabled;

    return invalid;
  };

  if (loading || (detailsLoading && isEdit)) {
    return <Loader />;
  }

  return (
    <>
      <PaymentVouchersForm
        isEdit={isEdit}
        lovs={lovs}
        setLovs={setLovs}
        values={values}
        setValues={setValues}
        errors={errors}
        setErrors={setErrors}
        tableData={tableData}
        setTableData={setTableData}
        setIsSubmitDisabled={setIsSubmitDisabled}
        businessPartnerId={businessPartnerId}
        classes={classes}
        disabled={isEdit}
      />
      <div>
        <AccountCurrency
          title={'Transactions'}
          lovs={lovs}
          values={values}
          setValues={setValues}
          systemCurrency={values.systemCurrencies.primary.Symbol}
          systemSecondaryCurrency={values.systemCurrencies.secondary.Symbol}
          disabled={isEdit}
        />

        <div className={classes.totalContainer}>
          <DifferenceSection
            credit={values.totalCredit}
            debit={values.totalDebit}
            systemCurrency={values.systemCurrencies.primary.Symbol}
          />
        </div>
        <TextAreaFormField
          name="note"
          title="Note"
          placeholder="Enter a note..."
          value={values.note || ''}
          onChange={(event) => {
            handleChange('note', event.target.value);
          }}
        />
        <div className={classes.documentSection}>
          <SupportingDocuments
            values={values}
            setValues={setValues}
            currentPaymentId={currentPaymentId}
            classes={classes}
          />
        </div>
        <div className={classes.buttonsContainer}>
          <EnhancedButton
            type="submit"
            onClick={handleSubmit}
            disabled={validateForm()}
            isPrimary
          >
            Submit
          </EnhancedButton>
        </div>
      </div>
    </>
  );
};

export default PaymentVoucherFormPopup;
